import { useState, useEffect, useRef } from 'react'
import DailyIframe from '@daily-co/daily-js';
import "./App.css";
import beams from "./images/beams.jpg";
import logo_new from "./images/logo-gamma-circular.png";
import Notification from './components/Notification';

const { REACT_APP_AVALON_API_URL, REACT_APP_CLINIC_URL } = process.env
const CLINIC_URL = REACT_APP_CLINIC_URL || 'https://clinic.avalonhealth.cloud'

function App() {
  const [notification, setNotification] = useState({ show: false, type: "success", title: "Success", message: "Successfully processed the transaction"})

  const [authToken, setAuthToken] = useState(null)
  const [meetingUrl, setMeetingUrl] = useState(null)
  const [providerOptions, setProviderOptions] = useState({show: false, patient: {avalonId: ''}})

  const callContainerRef = useRef(null)
  const callFrameRef = useRef(null)

  const [messages, setMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const messagesEndRef = useRef(null)

  const searchParams = new URL(window.location.href).searchParams;
  const meetingId = searchParams.get('meetingId');
  const user = searchParams.get('user');

  useEffect(() => {
    if (authToken && meetingUrl && callContainerRef.current && !callFrameRef.current) {
      const frame = DailyIframe.createFrame(callContainerRef.current, {
        showLeaveButton: true,
        showFullscreenButton: true,
        iframeStyle: {
          width: '100%',
          height: '100%',
          border: '0',
          borderRadius: '8px',
        },
      });
      callFrameRef.current = frame;

      const onAppMessage = (ev) => {
        const d = (ev && ev.data) || {};
        const isChat = d.type === 'chat-message' || d.type === 'chat' || d.event === 'chat-msg' || d.kind === 'chat';
        const text = d.message ?? d.text ?? d.body;
        if (isChat && text) {
          setMessages((prev) => [...prev, {
            id: d.id || `${Date.now()}-${Math.random()}`,
            text,
            senderName: d.senderName || d.name || d.fromName || 'Provider',
            senderRole: d.senderRole || d.senderType || 'provider',
            timestamp: d.timestamp || new Date().toISOString(),
            mine: false,
          }]);
        }
      };
      frame.on('app-message', onAppMessage);
      frame.join({ url: meetingUrl, token: authToken });

      return () => {
        frame.off('app-message', onAppMessage);
        frame.destroy();
        callFrameRef.current = null;
      };
    }
  }, [authToken, meetingUrl]);

  useEffect(() => {
      let mounted = true

      async function getAuthToken(){
          let jsonData = {
            meetingId: meetingId,
            user: user
          }

          const requestOptions = {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify(jsonData)
          };

          if (mounted){
              try{
                  const response = await fetch(REACT_APP_AVALON_API_URL+"/v2/live/meetingAuthToken", requestOptions)

                  if (response.ok){
                      const data = await response.json()

                      if (data.result === "success" && data.authToken && data.meetingUrl) {
                        const p = (data.providerOptions && data.providerOptions.patient) || {};
                        setProviderOptions({
                          show: !!(data.providerOptions && data.providerOptions.show),
                          patient: {
                            avalonId: p.avalon_id ?? p.avalonId ?? p.ehrId ?? '',
                            prefix: p.prefix ?? '',
                            firstName: p.first_name ?? p.firstName ?? '',
                            lastName: p.last_name ?? p.lastName ?? '',
                            dob: p.dob ?? '',
                            gender: p.gender ?? '',
                          }
                        })
                        setAuthToken(data.authToken)
                        setMeetingUrl(data.meetingUrl)
                        setNotification({ show: true, type: "success", title: "Ready", message: "Joining the meeting now" })
                      } else if (data.result === "success") {
                        setNotification({ show: true, type: "warning", title: "Failed", message: "This meeting can no longer be joined. Please ask your provider to send a new consultation link." })
                      } else {
                          setNotification({ show: true, type: "warning", title: "Failed", message: data.message })
                      }
                  } else {
                    console.log(`Request failed with status ${response.status}`);
                    throw new Error('API request failed');
                  }

              } catch (error) {
                  console.log(`Error: ${error}`);
                  setNotification({ show: true, type: "failed", title: "Error", message: "An error occurred. Failed to start your meeting. Please contact the support team at support@avalonhealth.cloud if this persists" })
              }
          }
      }

      meetingId && user && getAuthToken();

      return () => mounted = false

  }, [meetingId, user])

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendChat = () => {
    const text = chatInput.trim();
    const frame = callFrameRef.current;
    if (!text || !frame) return;
    let senderName = 'Patient';
    try {
      const local = frame.participants() && frame.participants().local;
      senderName = (local && local.user_name) || (user === 'provider' ? 'Provider' : 'Patient');
    } catch (e) {}
    const role = user === 'provider' ? 'provider' : 'patient';
    const id = `${Date.now()}-local`;
    const timestamp = new Date().toISOString();
    const msg = { type: 'chat-message', id, senderId: 'local', senderName, senderRole: role, senderType: role, message: text, timestamp, event: 'chat-msg', name: senderName };
    try { frame.sendAppMessage(msg, '*'); } catch (e) { return; }
    setMessages((prev) => [...prev, { id, text, senderName: `${senderName} (You)`, senderRole: role, timestamp, mine: true }]);
    setChatInput('');
  };

  return (
    <main className="flex justify-center gap-4 flex-col min-h-screen">
      <div className="relative flex min-h-screen flex-col justify-center overflow-hidden bg-gray-50">
        <img src={beams} alt="" className="absolute top-1/2 left-1/2 max-w-none -translate-x-1/2 -translate-y-1/2" width="1308" />

        <Notification notification={notification} setNotification={setNotification} />

        <div className="flex flex-col md:flex-row justify-center items-center min-h-screen mt-20">
          <div className="relative bg-white px-6 pt-10 pb-8 shadow-xl ring-1 ring-gray-900/5 sm:rounded-lg sm:px-10 w-full max-w-screen-xl md:w-3/4">
            <img src={logo_new} className="h-6 mb-4" alt="Avalon Health" />
            <h3>Avalon Health Virtual Consult</h3>
            <div className="divide-y divide-gray-300/50">
              <div className="space-y-6 py-8 text-base leading-7 text-gray-600">
                <p>Join the meeting below</p>
              </div>
              <div className="pt-3" style={{ height: '480px' }}>
                { authToken && meetingUrl ? <div ref={callContainerRef} style={{ height: '100%', width: '100%' }} /> : <p>Loading meeting...</p> }
              </div>
              { providerOptions.show && (
                <>
                  <div className="pt-8 text-base font-semibold leading-7">
                    <p className="text-gray-900">Actions:</p>
                    <div className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4 mb-4">
                      <button
                        type="button"
                        className="rounded-md bg-orange-500 hover:bg-orange-700 px-3.5 py-2.5 my-2 text-md font-semibold text-white shadow-sm hover:scale-105 transition-transform duration-200 ease-in-out"
                        onClick={() => (window.open(`${CLINIC_URL}/dictate/${providerOptions.patient.avalonId}`, '_blank'))}
                      >
                        <i className="fa fa-file" ></i> <i className="fa fa-plus" ></i> Create a Prescription
                      </button>
                      <button
                        type="button"
                        className="rounded-md bg-indigo-500 hover:bg-indigo-700 px-3.5 py-2.5 my-2 text-md font-semibold text-white shadow-sm hover:scale-105 transition-transform duration-200 ease-in-out"
                        onClick={() => (window.open(`${CLINIC_URL}/view/notes/${providerOptions.patient.avalonId}`, '_blank'))}
                      >
                        <i className="fa fa-plus" ></i> Create a Note
                      </button>
                      <button
                        type="button"
                        className="rounded-md bg-red-500 hover:bg-red-700 px-3.5 py-2.5 my-2 text-md font-semibold text-white shadow-sm hover:scale-105 transition-transform duration-200 ease-in-out"
                        onClick={() => (window.open(`${CLINIC_URL}/dictate/${providerOptions.patient.avalonId}`, '_blank'))}
                      >
                        <i className="fa fa-plus" ></i> Create a Letter
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 my-4">
                      <div className="p-4 shadow-lg rounded-xl text-[#0B0D23] bg-[#ECEAF6] border-2 border-[#0B0D23] hover:scale-105 transition-transform duration-200 ease-in-out">
                        <p className="text-gray-900">Patient Details:</p>
                        <p className="text-gray-900">{providerOptions.patient.prefix} {providerOptions.patient.firstName} {providerOptions.patient.lastName}</p>
                        <p className="text-gray-900">DOB: {providerOptions.patient.dob}</p>
                        <p className="text-gray-900">Gender: {providerOptions.patient.gender}</p>
                        <p className="text-gray-900">Avalon ID: {providerOptions.patient.avalonId}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="pt-8 text-base font-semibold leading-7">
                <p className="text-gray-900">Need help or want to speak to us</p>
                <p className="text-sky-500 hover:text-sky-600">Email: support@avalonhealth.cloud </p>
                <p className="text-sky-500 hover:text-sky-600">Phone: +27 646 394 007</p>
              </div>
            </div>
          </div>
          <div className="fixed md:relative bottom-0 md:bottom-auto right-0 md:right-auto top-auto md:top-0 w-full md:w-1/4 bg-white shadow-lg p-4 flex flex-col max-h-60 md:max-h-screen md:h-[560px] rounded-lg border border-gray-200">
            <h4 className="text-lg font-semibold mb-2">Chat</h4>
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {messages.length === 0 ? (
                <p className="text-gray-400 text-sm">Messages with your {user === 'provider' ? 'patient' : 'doctor'} will appear here.</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={m.mine ? 'text-right' : 'text-left'}>
                    <span className="block text-[11px] text-gray-400">{m.senderName}</span>
                    <span className={`inline-block px-3 py-1.5 rounded-2xl text-sm ${m.mine ? 'bg-sky-500 text-white' : 'bg-gray-100 text-gray-800'}`}>{m.text}</span>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
            <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); sendChat(); }}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                disabled={!authToken}
              />
              <button type="submit" className="rounded-md bg-sky-500 hover:bg-sky-600 text-white px-3 py-2 text-sm font-semibold disabled:opacity-50" disabled={!authToken || !chatInput.trim()}>Send</button>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}

export default App;
